import { formatDuration } from "../../../utils/dateHelpers";
import {TABLE_DATE_FORMAT} from "../../../utils/Constants";
import moment from "moment";
import {tokens} from "../../../public/tokens";

const dateToday = moment().format("YYYY-MM-DD");

export const emptyMilestone = () => ({
  id: null,
  template_milestone_id: null,
  name: "",
  sequence: 0,
  expected_date: "",
  cardinal_date: "",
  status: "Not Started",
});

export const emptyResource = () => ({
  id: null,
  template_resource_id: null,
  name: "",
  url: "",
  is_required: false,
});

/* Takes date and milestone, if milestone is not complete, and date is before today, return RED token color
  @param {string} date - date to check
  @param {string} milestone - milestone to check
*/
export const dateColorByMilestone = (date, milestone) => {
  if(!date) return tokens.colors.states.alert[500];
  if(moment(date).unix() < moment(dateToday).unix && milestone !== "Complete") {
    return tokens.colors.states.alert[500];
  }
}

// check if url is valid
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
/**
 * Derives project-milestone summary metrics for display in the project list.
 *
 * Calculates completion percentage, the next incomplete milestone and its date,
 * and duration/time-left/end-date values using expected dates first and cardinal
 * dates as a fallback.
 *
 * @param {Array<Object>} milestones - Array of milestone objects containing status, expected_date,
 * cardinal_date, sequence, and name values.
 * @param {string|Date} [startDate] - Project start date used to calculate duration.
 * @returns {Object} Summary object containing completion, next milestone details,
 * duration/time-left values for expected and cardinal dates, and end dates.
 * @returns {string} returns.completion - Completion percentage as a string such as "75%".
 * @returns {string|null} returns.nextDeliverableDate - Date for the next incomplete milestone.
 * @returns {string} returns.nextDeliverable - Name of the next incomplete milestone or "-".
 * @returns {string} returns.DurationExpected - Duration from startDate to the latest expected date.
 * @returns {string} returns.DurationCardinal - Duration from startDate to the latest cardinal date.
 * @returns {string} returns.TimeLeftExpected - Time remaining until the latest expected date.
 * @returns {string} returns.TimeLeftCardinal - Time remaining until the latest cardinal date.
 * @returns {string} returns.EndDateExpected - Latest expected end date or "-".
 * @returns {string} returns.EndDateCardinal - Latest cardinal end date or "-".
 */
export const processMilestoneData = (milestones, startDate) => {
    if (!milestones?.length) {
        return {
            completion: "0%",
            nextDeliverableDate: null,
            nextDeliverable: "-",
            estimateDuration: "-",
            estimateTimeLeft: "-",
            estimateEndDate: "-",
        };
    }

    const completedMilestones = milestones.filter(
        (milestone) => milestone.status === "Complete"
    );
    const incompleteMilestones = milestones.filter(
        (milestone) => milestone.status !== "Complete"
    );

    // Next deliverable:    
    const nextMilestone =
        [...incompleteMilestones]
            .filter((milestone) => milestone.expected_date && milestone.status !== "Complete")
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone1.expected_date).unix() - moment(milestone2.expected_date).unix()
            )[0] ||
        [...incompleteMilestones]
            .filter((milestone) => milestone.cardinal_date && milestone.status !== "Complete")
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone1.cardinal_date).unix() - moment(milestone2.cardinal_date).unix()
            )[0] ||
        [...incompleteMilestones].sort((milestone1, milestone2) => milestone1.sequence - milestone2.sequence)[0];

    const nextDeliverable = nextMilestone?.name || "-";
    const nextDeliverableDate = nextMilestone?.expected_date || nextMilestone?.cardinal_date || null;

    // Estimate end date: latest expected_date across all milestones; only fall
    // back to the latest cardinal_date if no milestone has an expected_date at all.
    const estimateEndMilestone =
        [...milestones]
            .filter((milestone) => milestone.expected_date)
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone2.expected_date).unix() - moment(milestone1.expected_date).unix()
            )[0]
    const estimateEndCardinalMilestone = [...milestones]
            .filter((milestone) => milestone.cardinal_date)
            .sort(
                (milestone1, milestone2) =>
                    moment(milestone2.cardinal_date).unix() - moment(milestone1.cardinal_date).unix()
            )[0];
    const EndDateExpected = estimateEndMilestone?.expected_date || null;
    const EndDateCardinal = estimateEndCardinalMilestone?.cardinal_date || null;

    const DurationExpected = (startDate && EndDateExpected)
        ? formatDuration(startDate.split("T")[0], EndDateExpected)
        : "-";

    const TimeLeftExpected = EndDateExpected
        ? formatDuration(moment().format('YYYY-MM-DD'), EndDateExpected)
        : "-";
    const Durationcardinal = (startDate && EndDateCardinal)
        ? formatDuration(startDate.split("T")[0], EndDateCardinal)
        : "-";
    const cardinalTimeLeftcardinal = EndDateCardinal
        ? formatDuration(moment().format('YYYY-MM-DD'), EndDateCardinal)
        : "-";

    return {
        completion: `${Math.round((completedMilestones.length / milestones.length) * 100)}%`,
        nextDeliverableDate: nextDeliverableDate,
        nextDeliverable: nextDeliverable,
        DurationExpected: DurationExpected,
        DurationCardinal: Durationcardinal,
        TimeLeftExpected: TimeLeftExpected,
        TimeLeftCardinal: cardinalTimeLeftcardinal,
        EndDateExpected: EndDateExpected || "-",
        EndDateCardinal: EndDateCardinal || "-",
    };
}

export const formatDate = (value) => {
  if (!value || !moment(value).isValid()) return "-";
  return moment(value).format(TABLE_DATE_FORMAT); // or "DD/MM/YYYY"
};
